import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { BankingEntitiesBankAccountNumberFormatEnum } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'

import { useBankInfoForm } from 'modules/employees/components/blocks/BankInfo/form'
import { getDirtyValues } from 'modules/employees/lib/getDirtyValues'
import type Employee from 'modules/employees/store/models/Employee'

interface Props {
  employee: Employee
  readonly: boolean
  setSubmitEventHandler: (eventHandler: () => void) => void
  setBankShowSubmit: (dirty: boolean) => void
  showHeader?: boolean
}

const BankSection: React.FC<Props> = ({
  employee,
  readonly,
  setSubmitEventHandler,
  setBankShowSubmit,
  showHeader = true,
}) => {
  const { form, showSubmit, submitting, bankAccountFormatOptions, accountLabel, extraFieldLabel } =
    useBankInfoForm(employee, readonly)

  const handleFieldChanged = (fieldName: string, fieldValue: string | undefined | number) => {
    form.setValues({ ...form.values, [fieldName]: fieldValue })
  }
  const handleFormatChange = (newFormat: BankingEntitiesBankAccountNumberFormatEnum) => {
    if (newFormat == 'other' || newFormat == 'clabe') {
      form.setValues({ ...form.values, complementaryData: '', format: newFormat })
    } else {
      handleFieldChanged('format', newFormat)
    }
  }

  useEffect(() => {
    setSubmitEventHandler(() => {
      track(trackingEvents.employee.editEmployeeInformationSubmit, {
        fields_edited: [
          ...getDirtyValues(
            Object.entries(form.values),
            Object.fromEntries(Object.entries(form.initialValues))
          ),
        ],
        section_edited: 'bank_information',
        legal_entity_country: employeeLegalEntity(employee).country,
        layout_version: 'v2',
      })

      form.handleSubmit()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.format, form.values.accountNumber, form.values.complementaryData])

  useEffect(() => {
    setBankShowSubmit(showSubmit)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSubmit])

  return (
    <Box gap="s24">
      {showHeader && (
        <Box
          flexDirection="row"
          paddingBottom="s8"
          border={{
            bottom: {
              width: 's1',
              style: 'solid',
              color: 'grey300',
            },
          }}
          justifyContent="spaceBetween"
        >
          <Text.Header2 color="grey1000" weight="medium">
            {i18n.t('employee_form.payroll_section.bank_account')}
          </Text.Header2>
        </Box>
      )}
      <Box flexDirection="row" gap="s24">
        {!readonly && (
          <Select
            label={i18n.t('employee_form.bank_info.format')}
            value={form.values.format}
            options={bankAccountFormatOptions()}
            onChange={(value) =>
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              handleFormatChange(value as BankingEntitiesBankAccountNumberFormatEnum)
            }
          />
        )}
        <TextField
          mandatory
          label={accountLabel}
          value={form.values.accountNumber}
          readonly={readonly}
          onChange={(value) => handleFieldChanged('accountNumber', value)}
          error={form.errors.accountNumber}
        />
        {extraFieldLabel && (
          <TextField
            mandatory
            label={extraFieldLabel}
            value={form.values.complementaryData}
            readonly={readonly}
            onChange={(value) => handleFieldChanged('complementaryData', value)}
            error={form.errors.complementaryData}
          />
        )}
      </Box>
    </Box>
  )
}

export default observer(BankSection)
